import { useMemo, useState } from "react";

import { useDebouncedValue } from "@mantine/hooks";

import { useResolvedReferenceDisplays } from "@/billing/hooks/useResolvedReferenceDisplays";
import { getOpenStatementsPayerReferences } from "@/billing/services/open-statements-payer-display";
import { OpenStatementsRow, OpenStatementsSearchOption } from "@/billing/types/open-statements";
import {
  filterOpenStatementsTableRows,
  normalizeSearchQuery,
} from "@/billing/utils/open-statements-table-filter";

/** Long enough that typing a name does not fire a payer resolution per character. */
export const OPEN_STATEMENTS_SEARCH_DEBOUNCE_MS = 300;

export interface UseOpenStatementsSearchResult {
  /** What the input shows: updated on every keystroke, ahead of the debounce. */
  query: string;
  setQuery: (query: string) => void;
  searchOption: OpenStatementsSearchOption;
  setSearchOption: (option: OpenStatementsSearchOption) => void;
  filteredRows: OpenStatementsRow[];
  /** True while payer names are being read for a payer search that has not resolved yet. */
  isResolvingPayers: boolean;
}

/**
 * Owns the Open Statements search: the query, the field it matches on, and the filtered rows.
 *
 * Client search filters on data the loader already resolved, so it is immediate. Payer search needs
 * `Invoice.recipient` resolved to a name first, which is a read per distinct recipient — those are
 * fetched on the first payer search and cached for the rest of the session, so only the first search
 * waits.
 *
 * The caller sorts and paginates what comes back. Filtering ahead of both is what keeps pagination
 * honest: page count and page contents are computed from the matching rows, so a search that narrows
 * the set to one page leaves no empty pages behind it.
 */
export function useOpenStatementsSearch(rows: OpenStatementsRow[]): UseOpenStatementsSearchResult {
  const [query, setQuery] = useState("");
  const [searchOption, setSearchOption] = useState<OpenStatementsSearchOption>(
    OpenStatementsSearchOption.Client,
  );
  const [debouncedQuery] = useDebouncedValue(query, OPEN_STATEMENTS_SEARCH_DEBOUNCE_MS);

  const payerQuery =
    searchOption === OpenStatementsSearchOption.Payer ? normalizeSearchQuery(debouncedQuery) : "";
  const payerReferences = useMemo(() => getOpenStatementsPayerReferences(rows), [rows]);
  // A changed payer query restarts the resolution, so a payer whose read failed is retried. Names
  // already resolved are skipped, so a repeat run costs no request.
  const { displays: payerDisplays, isResolving: isResolvingPayers } = useResolvedReferenceDisplays(
    payerReferences,
    { isEnabled: !!payerQuery, refreshKey: payerQuery },
  );

  const filterQuery = searchOption === OpenStatementsSearchOption.Payer ? debouncedQuery : query;

  const filteredRows = useMemo(
    () => filterOpenStatementsTableRows(rows, filterQuery, searchOption, payerDisplays),
    [rows, filterQuery, searchOption, payerDisplays],
  );

  return {
    query,
    setQuery,
    searchOption,
    setSearchOption,
    filteredRows,
    isResolvingPayers,
  };
}
